import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useReveal, useCountUp } from "@/hooks/useReveal";
import {
  MessageCircle, Bell, Zap, BarChart3, Camera, Handshake, MapPin, Sparkles,
  Users, Building2, TrendingUp, Star, Mic, Check, ArrowRight, Quote, ChevronDown,
  Radar, Target, Trophy, LineChart, Search, Bot,
} from "lucide-react";
import { useState, useEffect } from "react";

/* =========================================================================
   estatebroker.AI · Landing v2
   Design philosophy: mix light and dark sections, real product mockups,
   commercial-first copy (sell the numbers, not the poetry).
   Palette: navy #071129, ice #F4F6FA, cream #FBF7F0, blue #3B9EFF,
            hot pink accent #E63976 for money moments.
   ========================================================================= */

const AVATARS = [
  "https://i.pravatar.cc/80?img=12", "https://i.pravatar.cc/80?img=33",
  "https://i.pravatar.cc/80?img=47", "https://i.pravatar.cc/80?img=65",
  "https://i.pravatar.cc/80?img=23",
];

const AGENT_FEATURES = [
  { icon: MessageCircle, title: "Cadastro por áudio ou texto no WhatsApp", desc: "Manda 'nova lead: Fabíola quer 2 dorms em Barreiros até 500k' e pronto." },
  { icon: Radar, title: "Busca imóveis compatíveis sozinho", desc: "Novo lead entra, a IA já vasculha sua carteira e sugere as 3 melhores opções." },
  { icon: Bell, title: "Cobra follow-up no dia certo", desc: "'Fabíola tá parada há 4 dias, aqui a mensagem pra retomar.'" },
  { icon: Zap, title: "Copy pronta por etapa", desc: "Mensagem com nome do cliente e tom certo. Você copia e cola." },
  { icon: Camera, title: "Cria lead direto do print do Zap/OLX", desc: "Manda foto do anúncio do portal, a IA lê e cadastra tudo." },
  { icon: Handshake, title: "Agencia imóvel pela placa", desc: "Fotografou uma placa? Ela lê telefone e endereço e agencia." },
  { icon: Target, title: "Link rastreável do imóvel", desc: "Cliente abriu o anúncio? Você recebe alerta no WhatsApp na hora." },
  { icon: Sparkles, title: "Briefing matinal de 8h", desc: "Todo dia os leads urgentes com copy pronta, antes do café." },
];

const AGENCY_FEATURES = [
  { icon: Users, title: "Painel unificado do time", desc: "Todos os leads de todos os corretores em um lugar." },
  { icon: LineChart, title: "Rankings e conversão em tempo real", desc: "Quem tá vendendo mais, quem tá perdendo lead, quem responde no prazo." },
  { icon: Search, title: "Filtro por corretor, bairro, etapa", desc: "Encontra qualquer lead em segundos, mesmo com 500 na carteira." },
  { icon: Bot, title: "IA integrada ao site da imobiliária", desc: "Busca automática no seu site, com link direto pro cliente ver a ficha." },
];

const TESTIMONIALS = [
  { name: "Marina Bertelli", role: "Corretora autônoma", city: "Belo Horizonte, MG", creci: "CRECI 48.221",
    photo: "https://i.pravatar.cc/120?img=45", rating: 5,
    metric: "R$ 1.6M em VGV",
    quote: "Em 3 meses fechei 4 vendas com leads parados há mais de 60 dias. Eu nem lembrava que existiam. A IA lembrou por mim." },
  { name: "Rafael Andrade", role: "Corretor de lançamentos", city: "Recife, PE", creci: "CRECI 12.094",
    photo: "https://i.pravatar.cc/120?img=68", rating: 5,
    metric: "2x fechamentos/mês",
    quote: "Eu vivia no improviso. Hoje atendo 130 leads sem perder um follow-up. Meu fechamento mensal dobrou." },
  { name: "Donna SC Imóveis", role: "Imobiliária", city: "Florianópolis, SC", creci: "CRECI J-2.148",
    photo: "https://i.pravatar.cc/120?img=32", rating: 5,
    metric: "12 corretores no piloto",
    quote: "Antes cada corretor tinha seu método. Agora todos seguem o mesmo processo e eu enxergo o funil inteiro do meu escritório." },
];

const PLANS = [
  { id: "solo", name: "Solo", price: 67, blurb: "Pro autônomo que vende sozinho.",
    features: ["1 usuário", "Até 200 leads ativos", "IA no WhatsApp 24h", "Funil visual arrastável", "Copy pronta por etapa", "Link rastreável do imóvel", "Teste grátis de 7 dias"] },
  { id: "dupla", name: "Dupla", price: 117, blurb: "Pra parcerias e duplas de corretores.", highlight: true,
    features: ["2 usuários", "Leads ilimitados", "Tudo do Solo", "Carteira compartilhada", "Cruzamento entre carteiras", "Suporte prioritário", "Teste grátis de 7 dias"] },
  { id: "agencia", name: "Imobiliária", price: 297, blurb: "Pra escritório com 3+ corretores.",
    features: ["Até 10 corretores inclusos", "Painel do gestor consolidado", "Ranking + conversão por corretor", "Integração com o site da sua imobiliária", "Filtros por corretor e bairro", "Suporte dedicado por WhatsApp", "Onboarding 1:1 do time"] },
];

const FAQ = [
  { q: "Preciso baixar algum aplicativo?", a: "Não. Você usa o próprio WhatsApp que já tem no celular. O painel web é só pra quando quiser ver o funil completo. Funciona no navegador do celular também." },
  { q: "E se eu já tenho 200 leads?", a: "Você sobe a planilha em CSV ou Excel pelo painel e em segundos todos os leads estão dentro, prontos pra IA começar a trabalhar." },
  { q: "A IA entende áudio em português?", a: "Sim. Voz do corretor brasileiro, incluindo termos do mercado como FGTS, permuta, financiamento, planta, chaves." },
  { q: "Sirvo pra imobiliária com equipe?", a: "Sim. O plano Imobiliária tem painel do gestor com todos os corretores, filtros, rankings e integração com o site da sua empresa. A Donna SC roda piloto com 12 corretores conosco." },
  { q: "E aluguel?", a: "Também. Cadastra o tipo (venda ou aluguel), a IA adapta funil e copys." },
  { q: "Posso cancelar?", a: "Quando quiser. Sem multa, sem letra miúda. O teste de 7 dias não pede cartão." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className="min-h-screen bg-white text-[#071129] overflow-x-hidden font-body">
      <Nav scrolled={scrolled} onCtaClick={() => navigate("/auth/register")} />
      <Hero onCtaClick={() => navigate("/auth/register")} />
      <Metrics />
      <PanelShowcase />
      <Sales />
      <AgentPitch />
      <AgencyPitch onAgencyCta={() => navigate("/imobiliaria/register")} />
      <Testimonials />
      <Pricing onCtaClick={(plan) => navigate(`/auth/register?plan=${plan}`)} onAgencyCta={() => navigate("/imobiliaria/register")} />
      <FaqSection />
      <FinalCta onCtaClick={() => navigate("/auth/register")} />
      <Footer />
    </div>
  );
}

function BrandMark({ dark = false }) {
  return (
    <span className={`font-heading text-[22px] font-extrabold tracking-tight ${dark ? "text-white" : "text-[#071129]"}`}>
      estatebroker<span className="text-[#3B9EFF]">.AI</span>
    </span>
  );
}

/* ---------- NAV ---------- */
function Nav({ scrolled, onCtaClick }) {
  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-xl border-b border-stone-200 py-3" : "py-5"}`}>
      <div className="max-w-7xl mx-auto px-5 lg:px-10 flex items-center justify-between">
        <Link to="/"><BrandMark /></Link>
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-[#071129]/70">
          <a href="#painel" className="hover:text-[#071129] transition">O painel</a>
          <a href="#corretor" className="hover:text-[#071129] transition">Pro corretor</a>
          <a href="#imobiliaria" className="hover:text-[#071129] transition">Pra imobiliária</a>
          <a href="#planos" className="hover:text-[#071129] transition">Planos</a>
          <a href="#faq" className="hover:text-[#071129] transition">FAQ</a>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/auth/login" data-testid="nav-login-link" className="text-sm font-medium text-[#071129]/80 hover:text-[#071129] px-3 py-2 hidden sm:inline">Entrar</Link>
          <Button data-testid="nav-cta-trial" onClick={onCtaClick}
            className="bg-[#071129] hover:bg-[#0F1F3D] text-white rounded-full px-5 py-2.5 text-sm font-semibold">
            Teste grátis
          </Button>
        </div>
      </div>
    </header>
  );
}

/* ---------- HERO — light bg, editorial layout ---------- */
function Hero({ onCtaClick }) {
  return (
    <section className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 px-5 lg:px-10 overflow-hidden bg-white">
      {/* soft dotted texture */}
      <div className="absolute inset-0 opacity-[0.5] pointer-events-none"
           style={{ backgroundImage: 'radial-gradient(#071129 0.8px, transparent 0.8px)', backgroundSize: '28px 28px' }} />

      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-14 items-center relative">
        <div className="lg:col-span-7 animate-fade-up">
          <div className="inline-flex items-center gap-2 mb-6 text-xs font-bold uppercase tracking-[0.24em] text-[#3B9EFF]">
            <span className="w-8 h-px bg-[#3B9EFF]" /> A revolução silenciosa da corretagem
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-[72px] font-extrabold tracking-[-0.02em] leading-[1.02] text-[#071129]">
            Mais do que uma <span className="italic font-serif text-[#3B9EFF]">IA para Corretores</span>.
          </h1>

          <p className="mt-7 text-lg lg:text-xl text-[#071129]/70 leading-relaxed max-w-[640px]">
            O único assistente no WhatsApp e gestão de painel web que organiza seus leads, agenciamentos e ainda te ensina a fechar a venda.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <Button data-testid="hero-cta-trial" onClick={onCtaClick}
              className="bg-[#071129] hover:bg-[#0F1F3D] text-white px-8 py-6 text-base font-semibold rounded-full group shadow-lg">
              Começar teste grátis de 7 dias
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition" />
            </Button>
            <a href="#painel" data-testid="hero-cta-panel"
              className="px-8 py-[22px] text-base font-semibold rounded-full text-[#071129] border-2 border-[#071129]/15 hover:border-[#071129]/40 inline-flex items-center justify-center transition">
              Ver o painel em ação
            </a>
          </div>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex -space-x-3">
              {AVATARS.map((src, i) => <img key={i} src={src} alt="" loading="lazy" className="w-10 h-10 rounded-full border-2 border-white object-cover shadow-sm" />)}
            </div>
            <div className="text-sm text-[#071129]/70 leading-tight">
              <div className="flex items-center gap-1.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#3B9EFF] text-[#3B9EFF]" />)}
                <span className="ml-1 text-xs font-bold text-[#071129]">4.9</span>
              </div>
              <div className="font-semibold mt-0.5 text-[#071129]/90">
                Já usado por <span className="text-[#3B9EFF]">+120 corretores</span> e piloto na Donna SC
              </div>
            </div>
          </div>
        </div>

        {/* Right: layered dashboard screenshot mock (static) */}
        <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "0.15s", animationFillMode: "both" }}>
          <HeroMockup />
        </div>
      </div>
    </section>
  );
}

function HeroMockup() {
  return (
    <div className="relative max-w-[520px] mx-auto lg:mx-0">
      {/* Phone-style WhatsApp chat mock */}
      <div className="rounded-[32px] shadow-2xl shadow-[#071129]/25 bg-white overflow-hidden relative z-20 border border-stone-200">
        <div className="bg-[#075E54] text-white px-4 py-3.5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#3B9EFF] flex items-center justify-center font-bold text-sm text-white">EB</div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">estatebroker.AI</div>
            <div className="text-[10px] text-white/80 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-400 online-dot inline-block" /> online agora
            </div>
          </div>
        </div>
        <div className="chat-bg p-3.5 space-y-2 min-h-[340px]">
          <div className="bg-[#D9FDD3] rounded-lg rounded-tr-none px-3 py-2 ml-auto max-w-[80%] text-sm shadow-sm w-fit">
            <div className="flex items-center gap-2">
              <Mic className="w-3.5 h-3.5 text-stone-600" />
              <span className="text-stone-700 font-mono text-xs">0:12</span>
              <div className="flex gap-0.5 items-end ml-1">
                {[3,8,5,11,4,9,6,10,3,7,5,9].map((h,i) => <span key={i} className="w-0.5 bg-stone-500 rounded-full" style={{ height: `${h}px` }} />)}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg rounded-tl-none px-3 py-2 max-w-[86%] text-[13px] text-stone-800 shadow-sm">
            Anotado! <strong>Fabíola Gomes</strong>, apto 2 dorm, Barreiros, até R$500k, FGTS.<br/>
            Achei 3 imóveis compatíveis no site. Quer ver?
          </div>
          <div className="bg-[#D9FDD3] rounded-lg rounded-tr-none px-3 py-2 ml-auto max-w-[80%] text-[13px] text-stone-800 shadow-sm">Manda</div>
          <div className="bg-white rounded-lg rounded-tl-none px-3 py-2 max-w-[92%] text-[12px] text-stone-800 shadow-sm">
            <div className="font-semibold">1. 🏠 Apto Barreiros · 2q · 1v · 71m²</div>
            <div className="text-stone-600">📍 São José · 💰 R$ 485k</div>
            <div className="text-[#3B9EFF] font-mono text-[10px] mt-1">estatebroker.ai/l/a3f9c...</div>
          </div>
          <div className="bg-[#FFF4E4] border border-[#E63976]/25 rounded-lg px-3 py-2 max-w-[92%] text-[11px] shadow-sm">
            <div className="flex items-center gap-1.5 text-[#E63976] font-bold text-[10px] uppercase tracking-wider">🔥 Alerta agora</div>
            <div className="text-stone-800 mt-1">Fabíola abriu o link do imóvel #1 (2ª vez). Interesse quente, liga agora.</div>
          </div>
        </div>
      </div>

      {/* Floating: Panel snapshot */}
      <div className="absolute -right-4 lg:-right-16 -bottom-8 w-[280px] bg-white rounded-2xl shadow-2xl shadow-[#071129]/25 border border-stone-200 p-3.5 z-30 float-card-delay overflow-hidden">
        <div className="flex items-center justify-between mb-2.5">
          <div className="text-[9px] uppercase tracking-[0.18em] font-bold text-stone-500">Painel · Funil</div>
          <div className="flex gap-1">
            <span className="w-2 h-2 rounded-full bg-red-400/60" />
            <span className="w-2 h-2 rounded-full bg-yellow-400/60" />
            <span className="w-2 h-2 rounded-full bg-green-400/60" />
          </div>
        </div>
        <StaticKanban />
      </div>
    </div>
  );
}

function StaticKanban() {
  const COLS = [
    { label: "Novo", count: 12, tone: "bg-stone-100 text-stone-700", cards: [{n:"Fabíola G.", hot:true}, {n:"Marcos R."}] },
    { label: "Qualif.", count: 8, tone: "bg-[#3B9EFF]/15 text-[#1F7CE0]", cards: [{n:"Renata S."}] },
    { label: "Visita", count: 5, tone: "bg-emerald-100 text-emerald-700", cards: [{n:"Paulo T."}] },
  ];
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {COLS.map((c, ci) => (
        <div key={ci}>
          <div className={`text-[9px] font-bold px-1.5 py-1 rounded ${c.tone} text-center mb-1.5 uppercase tracking-wide flex items-center justify-between`}>
            <span>{c.label}</span><span className="opacity-60">{c.count}</span>
          </div>
          <div className="space-y-1">
            {c.cards.map((cd, i) => (
              <div key={i} className={`rounded px-1.5 py-1 text-[9px] font-medium ${cd.hot ? 'bg-emerald-50 border-2 border-emerald-400 text-emerald-900' : 'bg-stone-50 border border-stone-200 text-stone-800'}`}>
                <div>{cd.n}</div>
                {cd.hot && <div className="text-[8px] text-emerald-700 font-bold flex items-center gap-0.5">🔥 3× clique</div>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- METRICS BAND ---------- */
function Metrics() {
  return (
    <section className="bg-[#071129] py-10 border-y border-[#071129]">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
        <MetricItem value="+120" label="Corretores ativos" />
        <MetricItem value="R$ 47M" label="VGV em movimentação" />
        <MetricItem value="73%" label="Aumento no follow-up" />
        <MetricItem value="4.9★" label="Nota média" />
      </div>
    </section>
  );
}

function MetricItem({ value, label }) {
  return (
    <div>
      <div className="font-heading text-3xl lg:text-4xl font-extrabold text-white">{value}</div>
      <div className="text-xs text-white/60 uppercase tracking-widest font-semibold mt-1">{label}</div>
    </div>
  );
}

/* ---------- PANEL SHOWCASE — realistic static screenshot ---------- */
function PanelShowcase() {
  return (
    <section id="painel" className="py-24 lg:py-32 px-5 lg:px-10 bg-[#F4F6FA]">
      <div className="max-w-7xl mx-auto">
        <RevealBlock>
          <div className="grid lg:grid-cols-2 gap-6 items-end mb-12">
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-[#3B9EFF] font-bold mb-3">O painel de bastidor</div>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-[56px] font-bold tracking-[-0.01em] leading-[1.05] text-[#071129]">
                Seu funil inteiro, <span className="italic font-serif text-[#3B9EFF]">visualmente organizado</span>.
              </h2>
            </div>
            <p className="text-lg text-[#071129]/70 leading-relaxed">
              Enquanto a IA cuida do WhatsApp, o painel te dá o raio-X da carteira. Arrasta o lead entre etapas, filtra por bairro, sinaliza o cliente que abriu seu link agora mesmo.
            </p>
          </div>
        </RevealBlock>

        <RealPanelScreenshot />
      </div>
    </section>
  );
}

function RealPanelScreenshot() {
  const LEADS = {
    novo: [
      { name: "Fabíola Gomes", tag: "2 dorm · Barreiros", value: "R$ 485k", hot: 3, temp: "hot" },
      { name: "Marcos Rezende", tag: "3 dorm · Kobrasol", value: "R$ 620k", temp: "warm" },
      { name: "Beatriz Nunes", tag: "1 dorm · Centro", value: "R$ 340k", temp: "cold" },
      { name: "Cláudio Vieira", tag: "Casa · Coqueiros", value: "R$ 1.1M", temp: "warm" },
    ],
    qualificado: [
      { name: "Renata Silva", tag: "2 dorm · Estreito", value: "R$ 550k", hot: 5, temp: "hot" },
      { name: "Diego Ferrer", tag: "3 dorm · Trindade", value: "R$ 780k", temp: "warm" },
      { name: "Ana Paula", tag: "Apto · Jurerê", value: "R$ 1.4M", temp: "warm" },
    ],
    visita: [
      { name: "Paulo Torres", tag: "Casa · Ingleses", value: "R$ 890k", hot: 8, temp: "hot" },
      { name: "Camila Reis", tag: "3 dorm · Beira-mar", value: "R$ 1.2M", temp: "warm" },
    ],
    proposta: [
      { name: "Wagner M.", tag: "Cobertura · Canasvieiras", value: "R$ 2.1M", hot: 12, temp: "hot" },
    ],
    fechado: [
      { name: "Fam. Oliveira", tag: "Casa · Rio Vermelho", value: "R$ 780k · fechado", temp: "done" },
    ],
  };
  const COLS = [
    { id: "novo", label: "Novo", color: "border-stone-300", headBg: "bg-white" },
    { id: "qualificado", label: "Qualificado", color: "border-[#3B9EFF]/40", headBg: "bg-[#3B9EFF]/8" },
    { id: "visita", label: "Visita", color: "border-emerald-300", headBg: "bg-emerald-50" },
    { id: "proposta", label: "Proposta", color: "border-amber-300", headBg: "bg-amber-50" },
    { id: "fechado", label: "Fechado", color: "border-green-400", headBg: "bg-green-50" },
  ];

  return (
    <div className="rounded-3xl bg-white border border-stone-200 shadow-2xl shadow-[#071129]/15 overflow-hidden">
      {/* Panel top bar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-200 bg-stone-50">
        <div className="flex gap-1.5">
          <span className="w-3 h-3 rounded-full bg-red-400/60" />
          <span className="w-3 h-3 rounded-full bg-yellow-400/60" />
          <span className="w-3 h-3 rounded-full bg-green-400/60" />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="bg-white border border-stone-200 rounded-md px-3 py-1 text-[11px] text-stone-500 font-mono">app.estatebroker.ai / leads</div>
        </div>
        <div className="text-[10px] text-stone-500 hidden sm:block">Julho · 2026</div>
      </div>

      {/* Header + filter bar */}
      <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-heading text-lg font-bold text-[#071129]">Funil de leads</div>
          <div className="text-[11px] text-stone-500">30 leads · última sincronização agora</div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <div className="bg-stone-100 rounded px-2.5 py-1 text-[10px] text-stone-700 font-medium">Todos os bairros</div>
          <div className="bg-stone-100 rounded px-2.5 py-1 text-[10px] text-stone-700 font-medium">Todas as temperaturas</div>
          <div className="bg-[#071129] rounded px-2.5 py-1 text-[10px] text-white font-medium">+ Novo lead</div>
        </div>
      </div>

      <div className="p-4 lg:p-5 overflow-x-auto">
        <div className="flex gap-3 min-w-[900px]">
          {COLS.map((c) => (
            <div key={c.id} className={`flex-1 min-w-[170px] rounded-lg border ${c.color} bg-stone-50/50 p-2`}>
              <div className={`flex items-center justify-between mb-2 px-2 py-1.5 rounded ${c.headBg} border ${c.color}`}>
                <div className="text-[10px] font-bold uppercase tracking-wide text-[#071129]">{c.label}</div>
                <div className="text-[10px] font-mono text-stone-500">{LEADS[c.id].length}</div>
              </div>
              <div className="space-y-1.5">
                {LEADS[c.id].map((l, i) => (
                  <div key={i} className={`bg-white rounded-md p-2 border shadow-sm ${l.hot ? 'border-emerald-400 border-2' : 'border-stone-200'}`}>
                    <div className="flex items-center gap-1">
                      <span className={`w-1.5 h-1.5 rounded-full ${l.temp==='hot'?'bg-red-500':l.temp==='warm'?'bg-amber-400':l.temp==='cold'?'bg-blue-400':'bg-emerald-500'}`} />
                      <div className="text-[11px] font-semibold text-[#071129] truncate">{l.name}</div>
                    </div>
                    <div className="text-[10px] text-stone-500 mt-0.5">{l.tag}</div>
                    <div className="text-[10px] text-[#071129] mt-1 font-mono">{l.value}</div>
                    {l.hot && (
                      <div className="mt-1.5 px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 flex items-center gap-1">
                        <span className="text-[9px] font-bold text-emerald-800">🔥 {l.hot}× clique</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom legend */}
      <div className="px-5 py-3 border-t border-stone-100 flex flex-wrap gap-4 text-[10px] text-stone-500">
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /> Quente</div>
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" /> Morno</div>
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-400" /> Frio</div>
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Cliente clicou</div>
        <div className="ml-auto text-emerald-700 font-medium">Real-time sync com WhatsApp ✓</div>
      </div>
    </div>
  );
}

/* ---------- SALES / MONEY (replaces the "perdendo processo" section) ---------- */
function Sales() {
  const [r1, count5] = useCountUp(5);
  const [r2, count73] = useCountUp(73);
  const [r3, count80] = useCountUp(80);
  return (
    <section className="py-24 lg:py-32 px-5 lg:px-10 bg-[#FBF7F0] relative overflow-hidden">
      <div className="absolute -left-32 -top-32 w-96 h-96 bg-[#E63976]/6 rounded-full blur-3xl" />
      <div className="max-w-6xl mx-auto relative">
        <RevealBlock>
          <div className="text-xs uppercase tracking-[0.28em] text-[#E63976] font-bold mb-4 text-center">A conta é simples</div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-[56px] font-bold tracking-[-0.01em] leading-[1.05] text-center max-w-4xl mx-auto text-[#071129]">
            Um único fechamento paga o<br/><span className="italic font-serif text-[#E63976]">estatebroker.AI por mais de um ano</span>.
          </h2>
          <p className="mt-6 text-center text-lg text-[#071129]/70 max-w-2xl mx-auto">
            Você não tá pagando por uma ferramenta. Tá comprando o método que fecha a venda que hoje escapa.
          </p>
        </RevealBlock>
        <div className="grid md:grid-cols-3 gap-5 mt-16">
          <SalesCard innerRef={r1} big={`${count5}×`} title="Contatos para fechar" desc="80% das vendas acontecem depois do 5º contato. A maioria dos corretores desiste no 2º." />
          <SalesCard innerRef={r2} big={`${count73}%`} title="Aumento no follow-up" desc="Média dos corretores que usam o estatebroker.AI nos primeiros 90 dias." featured />
          <SalesCard innerRef={r3} big={`R$ ${count80}k`} title="Comissão média por venda" desc="Uma única venda de imóvel de R$ 800k a 6% já paga 12 meses do plano Solo." />
        </div>
      </div>
    </section>
  );
}

function SalesCard({ big, title, desc, featured, innerRef }) {
  return (
    <div ref={innerRef} className={`relative rounded-2xl p-8 transition hover:-translate-y-1 ${featured ? "bg-[#071129] text-white shadow-2xl shadow-[#071129]/20 lg:-translate-y-4" : "bg-white border border-stone-200 text-[#071129]"}`}>
      <div className={`font-heading text-6xl lg:text-7xl font-extrabold leading-none ${featured ? "text-[#3B9EFF]" : "text-[#E63976]"}`}>
        {big}
      </div>
      <div className={`font-heading font-bold text-lg mt-4 ${featured ? "text-white" : "text-[#071129]"}`}>{title}</div>
      <p className={`mt-2 text-sm leading-relaxed ${featured ? "text-white/70" : "text-[#071129]/60"}`}>{desc}</p>
    </div>
  );
}

/* ---------- AGENT PITCH — corretor autônomo ---------- */
function AgentPitch() {
  return (
    <section id="corretor" className="py-24 lg:py-32 px-5 lg:px-10 bg-white">
      <div className="max-w-7xl mx-auto">
        <RevealBlock>
          <div className="text-xs uppercase tracking-[0.28em] text-[#3B9EFF] font-bold mb-4">Pra corretor autônomo</div>
          <div className="grid lg:grid-cols-2 gap-8 items-end mb-14">
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-[56px] font-bold tracking-[-0.01em] leading-[1.05] text-[#071129]">
              Tudo o que sua rotina exige. <span className="italic font-serif text-[#3B9EFF]">E mais.</span>
            </h2>
            <p className="text-lg text-[#071129]/70 leading-relaxed">
              Sem ferramenta nova pra aprender. Sem formulário pra preencher. Você conversa por WhatsApp e a IA cuida do resto.
            </p>
          </div>
        </RevealBlock>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {AGENT_FEATURES.map((f, i) => <MiniFeature key={i} {...f} delay={i * 50} />)}
        </div>
      </div>
    </section>
  );
}

function MiniFeature({ icon: Icon, title, desc, delay }) {
  const [ref, visible] = useReveal(0.12);
  return (
    <div ref={ref} className={`reveal ${visible ? "is-visible" : ""} p-6 border border-stone-200 rounded-2xl hover:border-[#3B9EFF]/50 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 bg-white`} style={{ transitionDelay: `${delay}ms` }}>
      <div className="w-10 h-10 rounded-lg bg-[#3B9EFF]/10 text-[#3B9EFF] flex items-center justify-center mb-4">
        <Icon className="w-5 h-5" />
      </div>
      <div className="font-heading font-bold text-[15px] text-[#071129] leading-snug">{title}</div>
      <p className="text-sm text-[#071129]/60 mt-2 leading-relaxed">{desc}</p>
    </div>
  );
}

/* ---------- AGENCY PITCH — dark section ---------- */
function AgencyPitch({ onAgencyCta }) {
  return (
    <section id="imobiliaria" className="py-24 lg:py-32 px-5 lg:px-10 bg-[#071129] text-white relative overflow-hidden">
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#3B9EFF]/8 rounded-full blur-3xl" />
      <div className="max-w-7xl mx-auto relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 mb-4 text-xs font-bold uppercase tracking-[0.24em] text-[#3B9EFF]">
              <Building2 className="w-4 h-4" /> Pra imobiliária
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-[56px] font-bold tracking-[-0.01em] leading-[1.05]">
              Um <span className="italic font-serif text-[#3B9EFF]">método padronizado</span> pra toda equipe.
            </h2>
            <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-lg">
              O gestor vê o funil de todos os corretores em tempo real. Cada corretor tem sua própria carteira mas segue o mesmo processo. E a IA busca imóveis direto no site da imobiliária.
            </p>
            <div className="mt-8 space-y-3">
              {AGENCY_FEATURES.map((f, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#3B9EFF]/15 border border-[#3B9EFF]/30 flex items-center justify-center flex-shrink-0">
                    <f.icon className="w-4 h-4 text-[#3B9EFF]" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{f.title}</div>
                    <div className="text-sm text-white/60 mt-0.5">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Button data-testid="agency-hero-cta" onClick={onAgencyCta}
                className="bg-[#3B9EFF] hover:bg-[#1F7CE0] text-white px-8 py-6 text-base font-semibold rounded-full group">
                Cadastrar minha imobiliária
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition" />
              </Button>
              <a href="mailto:contato@estatebroker.ai" className="px-8 py-[22px] text-base font-semibold rounded-full text-white border-2 border-white/20 hover:border-white/40 inline-flex items-center justify-center transition">
                Falar com um humano
              </a>
            </div>
          </div>
          <div className="lg:col-span-6">
            <AgencyDashboardMockup />
          </div>
        </div>
      </div>
    </section>
  );
}

function AgencyDashboardMockup() {
  const REALTORS = [
    { name: "Fernanda M.", leads: 47, closed: 4, initials: "FM" },
    { name: "Roberto L.", leads: 38, closed: 3, initials: "RL" },
    { name: "Camila V.", leads: 31, closed: 2, initials: "CV" },
    { name: "André S.", leads: 28, closed: 2, initials: "AS" },
  ];
  return (
    <div className="rounded-3xl bg-white text-[#071129] shadow-2xl shadow-[#071129]/40 overflow-hidden border border-white/10">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-200 bg-stone-50">
        <div className="flex gap-1.5">
          <span className="w-3 h-3 rounded-full bg-red-400/60" />
          <span className="w-3 h-3 rounded-full bg-yellow-400/60" />
          <span className="w-3 h-3 rounded-full bg-green-400/60" />
        </div>
        <div className="flex-1 flex justify-center">
          <div className="bg-white border border-stone-200 rounded-md px-3 py-1 text-[11px] text-stone-500 font-mono">app.estatebroker.ai / imobiliaria</div>
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#3B9EFF] font-bold">Painel · Gestor</div>
            <div className="font-heading text-lg font-extrabold text-[#071129]">Donna SC · visão geral</div>
          </div>
          <div className="text-[10px] text-stone-500">12 corretores · 216 leads</div>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="p-3 rounded-lg bg-[#3B9EFF]/8 border border-[#3B9EFF]/25">
            <div className="text-[9px] uppercase font-bold text-[#3B9EFF]">VGV mês</div>
            <div className="font-heading text-2xl font-extrabold text-[#071129]">R$ 4.7M</div>
          </div>
          <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
            <div className="text-[9px] uppercase font-bold text-emerald-700">Fechamentos</div>
            <div className="font-heading text-2xl font-extrabold text-[#071129]">11</div>
          </div>
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
            <div className="text-[9px] uppercase font-bold text-amber-700">Parados 7d+</div>
            <div className="font-heading text-2xl font-extrabold text-[#071129]">23</div>
          </div>
        </div>

        {/* Top realtors */}
        <div className="text-[10px] uppercase font-bold text-stone-500 mb-2">Top corretores</div>
        <div className="space-y-2">
          {REALTORS.map((r, i) => (
            <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg border border-stone-200 hover:border-[#3B9EFF]/40 transition">
              <div className="w-8 h-8 rounded-full bg-[#3B9EFF] text-white flex items-center justify-center text-xs font-bold">{r.initials}</div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-[#071129]">{r.name}</div>
                <div className="text-[10px] text-stone-500">{r.leads} leads · {r.closed} fechados</div>
              </div>
              <div className="text-[10px] font-mono text-[#3B9EFF] font-bold">#{i + 1}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- TESTIMONIALS ---------- */
function Testimonials() {
  return (
    <section className="py-24 lg:py-32 px-5 lg:px-10 bg-[#F4F6FA]">
      <div className="max-w-7xl mx-auto">
        <RevealBlock>
          <div className="text-xs uppercase tracking-[0.28em] text-[#3B9EFF] font-bold mb-4 text-center">Provas sociais</div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-[56px] font-bold tracking-[-0.01em] text-center leading-[1.05] max-w-3xl mx-auto text-[#071129]">
            Corretor <span className="italic font-serif text-[#3B9EFF]">falando com corretor</span>.
          </h2>
        </RevealBlock>
        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {TESTIMONIALS.map((t, i) => <TestimonialCard key={i} {...t} delay={i * 100} />)}
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({ name, role, city, creci, photo, rating, quote, metric, delay }) {
  const [ref, visible] = useReveal(0.1);
  return (
    <div ref={ref} className={`reveal ${visible ? "is-visible" : ""} bg-white rounded-2xl p-7 border border-stone-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-500 flex flex-col`} style={{ transitionDelay: `${delay}ms` }}>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center gap-0.5">
          {[...Array(rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#3B9EFF] text-[#3B9EFF]" />)}
        </div>
        {metric && <span className="ml-auto px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 text-[10px] font-bold">{metric}</span>}
      </div>
      <Quote className="w-6 h-6 text-[#3B9EFF]/40 mb-2" strokeWidth={2.5} />
      <p className="text-[15px] text-[#071129]/85 leading-relaxed flex-1 italic">&ldquo;{quote}&rdquo;</p>
      <div className="flex items-center gap-3 mt-5 pt-5 border-t border-stone-200">
        <img src={photo} alt={name} loading="lazy" className="w-11 h-11 rounded-full object-cover" />
        <div>
          <div className="font-semibold text-sm text-[#071129]">{name}</div>
          <div className="text-xs text-[#071129]/60">{role} · {city}</div>
          <div className="text-[10px] text-[#071129]/40 font-mono tracking-wider mt-0.5">{creci}</div>
        </div>
      </div>
    </div>
  );
}

/* ---------- PRICING ---------- */
function Pricing({ onCtaClick, onAgencyCta }) {
  return (
    <section id="planos" className="py-24 lg:py-32 px-5 lg:px-10 bg-white">
      <div className="max-w-7xl mx-auto">
        <RevealBlock>
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-[0.28em] text-[#3B9EFF] font-bold mb-4">Planos</div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-[56px] font-bold tracking-[-0.01em] leading-[1.05] text-[#071129]">
              Escolha do <span className="italic font-serif text-[#3B9EFF]">seu jeito</span>.
            </h2>
            <p className="mt-5 text-lg text-[#071129]/70">Todos com 7 dias grátis. Cancele quando quiser.</p>
          </div>
        </RevealBlock>
        <div className="grid md:grid-cols-3 gap-5 mt-14 items-start">
          {PLANS.map((p, i) => <PlanCard key={p.id} plan={p} onCta={() => p.id === "agencia" ? onAgencyCta() : onCtaClick(p.id)} delay={i * 100} />)}
        </div>
      </div>
    </section>
  );
}

function PlanCard({ plan, onCta, delay }) {
  const [ref, visible] = useReveal(0.1);
  const p = plan;
  return (
    <div ref={ref} className={`reveal ${visible ? "is-visible" : ""} relative rounded-3xl p-8 transition-all duration-500 ${p.highlight ? "bg-[#071129] text-white shadow-2xl shadow-[#071129]/25 lg:-translate-y-4" : "bg-white border border-stone-200 text-[#071129] hover:-translate-y-1 hover:shadow-xl"}`} style={{ transitionDelay: `${delay}ms` }} data-testid={`plan-card-${p.id}`}>
      {p.highlight && <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#3B9EFF] text-white text-[11px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider">Mais escolhido</div>}
      <div className="font-heading text-2xl font-extrabold">{p.name}</div>
      <p className={`text-sm mt-1.5 ${p.highlight ? "text-white/70" : "text-[#071129]/60"}`}>{p.blurb}</p>
      <div className="mt-6 flex items-baseline gap-1.5">
        <span className={`text-sm ${p.highlight ? "text-white/70" : "text-[#071129]/60"}`}>R$</span>
        <span className="font-heading text-6xl font-extrabold tracking-[-0.02em]">{p.price}</span>
        <span className={`text-sm ${p.highlight ? "text-white/70" : "text-[#071129]/60"}`}>/mês</span>
      </div>
      <ul className="mt-7 space-y-2.5">
        {p.features.map((f, i) => <li key={i} className="flex gap-2.5 text-sm">
          <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.highlight ? "text-[#3B9EFF]" : "text-[#3B9EFF]"}`} />
          <span>{f}</span>
        </li>)}
      </ul>
      <Button data-testid={`plan-cta-${p.id}`} onClick={onCta} className={`w-full mt-8 rounded-full py-6 font-semibold ${p.highlight ? "bg-[#3B9EFF] hover:bg-[#1F7CE0] text-white" : "bg-[#071129] hover:bg-[#0F1F3D] text-white"}`}>
        {p.id === "agencia" ? "Cadastrar imobiliária" : "Começar grátis"}
      </Button>
    </div>
  );
}

/* ---------- FAQ ---------- */
function FaqSection() {
  return (
    <section id="faq" className="py-24 lg:py-32 px-5 lg:px-10 bg-[#FBF7F0]">
      <div className="max-w-3xl mx-auto">
        <RevealBlock>
          <div className="text-xs uppercase tracking-[0.28em] text-[#3B9EFF] font-bold mb-4 text-center">Dúvidas frequentes</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-[-0.01em] text-center mb-12 leading-[1.05] text-[#071129]">Perguntas que todo <span className="italic font-serif text-[#3B9EFF]">corretor faz</span>.</h2>
        </RevealBlock>
        <div className="space-y-3">{FAQ.map((item, i) => <FaqItem key={i} {...item} />)}</div>
      </div>
    </section>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-stone-200 rounded-xl bg-white overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-stone-50 transition">
        <span className="font-semibold text-base text-[#071129]">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#3B9EFF] transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden"><p className="px-6 pb-5 text-[#071129]/70 leading-relaxed">{a}</p></div>
      </div>
    </div>
  );
}

/* ---------- FINAL CTA — dark ---------- */
function FinalCta({ onCtaClick }) {
  const [ref, visible] = useReveal(0.2);
  return (
    <section className="py-24 lg:py-32 px-5 lg:px-10 bg-[#071129]">
      <div ref={ref} className={`max-w-5xl mx-auto rounded-[40px] bg-gradient-to-br from-[#0F1F3D] to-[#071129] border border-[#3B9EFF]/25 p-10 lg:p-20 text-center relative overflow-hidden reveal ${visible ? "is-visible" : ""}`}>
        <div className="absolute -top-32 -left-32 w-80 h-80 bg-[#3B9EFF]/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-[#E63976]/15 rounded-full blur-3xl" />
        <div className="relative">
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-[56px] font-extrabold text-white tracking-[-0.01em] leading-[1.05]">
            A próxima venda começa<br/><span className="italic font-serif text-[#3B9EFF]">com o próximo follow-up</span>.
          </h2>
          <p className="text-white/80 mt-6 text-lg max-w-xl mx-auto">Liga a IA agora. Em 2 minutos ela já tá trabalhando seus leads.</p>
          <Button data-testid="final-cta-trial" onClick={onCtaClick} className="mt-8 bg-[#3B9EFF] hover:bg-[#1F7CE0] text-white px-10 py-7 text-lg font-semibold rounded-full group shadow-2xl shadow-[#3B9EFF]/30">
            Quero meus 7 dias grátis
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition" />
          </Button>
          <p className="text-white/50 text-xs mt-5">7 dias grátis · Cancele quando quiser</p>
        </div>
      </div>
    </section>
  );
}

/* ---------- FOOTER ---------- */
function Footer() {
  return (
    <footer className="px-5 lg:px-10 py-12 border-t border-stone-200 bg-[#071129]">
      <div className="max-w-7xl mx-auto grid sm:grid-cols-2 gap-6 items-center text-sm text-white/50">
        <div>
          <Link to="/" className="block mb-2"><BrandMark dark /></Link>
          <p>© 2026 estatebroker.AI. Feito pra corretor brasileiro.</p>
        </div>
        <div className="flex gap-6 sm:justify-end flex-wrap">
          <a href="#planos" className="hover:text-white">Planos</a>
          <a href="#imobiliaria" className="hover:text-white">Pra imobiliária</a>
          <a href="#faq" className="hover:text-white">FAQ</a>
          <Link to="/auth/login" className="hover:text-white">Entrar</Link>
          <Link to="/imobiliaria/register" className="hover:text-[#3B9EFF]">Cadastrar imobiliária</Link>
        </div>
      </div>
    </footer>
  );
}

function RevealBlock({ children, className = "" }) {
  const [ref, visible] = useReveal(0.1);
  return <div ref={ref} className={`reveal ${visible ? "is-visible" : ""} ${className}`}>{children}</div>;
}
